<template>
  <div class="space-y-3">
    <PromocodeSwitcher
      v-model="switchedOn"
      :is-loading="isPromocodeApplying || campaignPromocode.isLoading"
    />
    <PromocodeForm
      v-show="switchedOn"
      v-model="promocode"
      :error-message="errorMessage"
      :is-loading="isPromocodeApplying || campaignPromocode.isLoading"
      @onSubmit="handleFormSubmit"
      @onFocus="resetValidations"
    />
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, watch, onMounted } from 'vue';
  import useVuelidate from '@vuelidate/core';
  import { minLength } from '@vuelidate/validators';
  import { isAxiosError } from 'axios';
  import { isArray, isString } from 'lodash-es';
  import { useI18n } from 'vue-i18n';
  import { validationI18n } from 'shared';
  import type { Airflow } from '@aviata/platform-types';
  import type { flightEntity } from 'freedom-shared';

  import { useApplyPromocodeMutation } from '../api';
  import { useCampaignPromocode } from '../model';
  import PromocodeForm from './PromocodeForm.vue';
  import PromocodeSwitcher from './PromocodeSwitcher.vue';

  const props = defineProps<{
    orderId: string;
    billId: string;
    hasActivePromocode: boolean;
    flights: flightEntity.types.MappedFlight[];
    bookings: Airflow.Product.BookingData[];
  }>();

  const emit = defineEmits<{ onAutomaticApply: [] }>();

  const i18n = useI18n();
  const { required, withI18nMessage } =
    validationI18n.model.useLocalizedValidators('promocode.validations');

  const switchedOn = ref(false);
  const promocode = ref('');

  const campaignPromocode = useCampaignPromocode(
    computed(() => props.flights),
    computed(() => props.bookings),
    computed(() => props.hasActivePromocode),
  );

  const {
    mutate: apply,
    isLoading: isPromocodeApplying,
    error,
    reset: resetApplyMutation,
  } = useApplyPromocodeMutation(props.orderId, props.billId);

  const $externalResults = computed(() => {
    if (!error.value) {
      return {};
    }

    if (
      isAxiosError(error.value) &&
      isArray(error.value.response?.data.errors) &&
      error.value.response?.data.errors[0] &&
      isString(error.value.response.data.errors[0].message)
    ) {
      return { promocode: [error.value.response.data.errors[0].message] };
    }

    return i18n.t('promocode.defaultError');
  });

  const v$ = useVuelidate(
    { promocode: { required, minLength: withI18nMessage(minLength(6)) } },
    { promocode },
    // @ts-expect-error TODO: fix typing of $externalResults (works fine rn, assuming error message from failed response is always a string)
    { $externalResults },
  );

  const errorMessage = computed(() => {
    if (!v$.value.$error) {
      return;
    }

    return v$.value.$errors[0].$message as string;
  });

  function resetValidations() {
    resetApplyMutation();
    v$.value.$reset();
  }

  function handleFormSubmit(isAutoApply?: boolean) {
    resetApplyMutation();
    v$.value.$touch();
    if (v$.value.$error) {
      return;
    }

    apply(promocode.value);
    if (isAutoApply) {
      emit('onAutomaticApply');
    }
  }

  const automaticallyApplyPromocode = (promocodeValue: string) => {
    promocode.value = promocodeValue;
    switchedOn.value = true;
    handleFormSubmit(true);
  };

  /** In case if we get RC response later than order from backend.  */
  watch(
    () => campaignPromocode.value.value,
    newValue => {
      if (newValue) {
        automaticallyApplyPromocode(newValue);
      }
    },
  );

  onMounted(() => {
    if (campaignPromocode.shouldApplyCampaignPromocode()) {
      automaticallyApplyPromocode(campaignPromocode.value.value);
    }
  });
</script>
